import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { BookOpen, Loader2, MessageCircle, Sparkles, Zap, Heart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHUNK_TYPE_CONFIG = {
  phrase: { icon: MessageCircle, label: "Phrase", color: "bg-blue-100 text-blue-700" },
  grammar: { icon: BookOpen, label: "Grammar", color: "bg-purple-100 text-purple-700" },
  verb_combo: { icon: Zap, label: "Verb Combo", color: "bg-orange-100 text-orange-700" },
  slang: { icon: Sparkles, label: "Slang", color: "bg-pink-100 text-pink-700" },
  expression: { icon: Heart, label: "Expression", color: "bg-green-100 text-green-700" }
};

// Animation configs extracted to prevent re-renders
const modalAnimation = {
  initial: { opacity: 0, scale: 0.95 },
  animate: { opacity: 1, scale: 1 },
  exit: { opacity: 0, scale: 0.95 }
};

const cardAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

// Pre-defined transition configs for cards to prevent re-renders
const cardTransitions = Array.from({ length: 20 }, (_, i) => ({ delay: i * 0.1 }));

export default function ChunkAnalyzer({ lyrics, onClose }) {
  const [chunks, setChunks] = useState(null);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAnalyzed, setHasAnalyzed] = useState(false);

  const analyzeChunks = useCallback(async () => {
    if (!lyrics) {
      toast.error("No lyrics to analyze");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/lyrics/extract-chunks`, {
        lyrics,
        source_language: "Spanish"
      });
      
      setChunks(response.data.chunks);
      setSummary(response.data.summary);
      setHasAnalyzed(true);
    } catch (err) {
      toast.error("Couldn't analyze lyrics");
    } finally {
      setIsLoading(false);
    }
  }, [lyrics]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        {...modalAnimation}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
                <BookOpen className="w-5 h-5 text-[var(--fg-default)]" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Learn from this Song</h2>
                <p className="text-sm text-[var(--fg-muted)]">Practical chunks you can use in real life</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              className="rounded-full"
            >
              Close
            </Button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 pr-3 scrollbar-visible" style={{ scrollbarGutter: 'stable' }}>
          {!hasAnalyzed ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-[var(--bg-subtle)] rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-[var(--brand-primary)]" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Extract Useful Spanish</h3>
              <p className="text-[var(--fg-muted)] mb-6 max-w-md mx-auto">
                We&apos;ll find phrases, grammar patterns, and expressions from these lyrics that you can actually use in conversation.
              </p>
              <Button
                onClick={analyzeChunks}
                disabled={isLoading}
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white rounded-full px-8 py-3"
                data-testid="analyze-chunks-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Extract Chunks
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {summary && (
                <div className="bg-[var(--bg-subtle)] p-4 rounded-2xl mb-6">
                  <p className="text-sm text-[var(--fg-muted)]">
                    <span className="font-semibold">Takeaway:</span> {summary}
                  </p>
                </div>
              )}

              <AnimatePresence>
                {chunks?.map((chunk, index) => (
                  <ChunkCard key={index} chunk={chunk} index={index} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

function ChunkCard({ chunk, index }) {
  const config = CHUNK_TYPE_CONFIG[chunk.chunk_type] || CHUNK_TYPE_CONFIG.phrase;
  const Icon = config.icon;
  // Use pre-computed transition to avoid inline object recreation
  const transition = cardTransitions[index] || cardTransitions[cardTransitions.length - 1];

  return (
    <motion.div
      {...cardAnimation}
      transition={transition}
      className="bg-white border-2 border-gray-100 rounded-2xl p-5 hover:border-[var(--brand-primary)]/30 transition-colors"
      data-testid={`chunk-card-${index}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="text-lg font-bold text-[var(--fg-default)]">{chunk.spanish}</h3>
          <p className="text-[var(--brand-primary)] font-medium">{chunk.english_meaning}</p>
        </div>
        <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${config.color}`}>
          <Icon className="w-3 h-3" />
          {config.label}
        </span>
      </div>

      {/* Why it's useful */}
      <p className="text-sm text-[var(--fg-muted)] mb-4">{chunk.why_useful}</p>

      {/* Example */}
      <div className="bg-[var(--bg-subtle)] p-3 rounded-xl">
        <p className="text-sm font-medium text-[var(--fg-default)]">&ldquo;{chunk.example_spanish}&rdquo;</p>
        <p className="text-sm text-[var(--fg-muted)] italic mt-1">→ &ldquo;{chunk.example_english}&rdquo;</p>
      </div>
    </motion.div>
  );
}
